import { FormSectionStateArray, FormState, PowerVm } from '@navstation-portal/reporting/data-model/vessel-report';
import {
  CargoPumpMeterFormState,
  HasPreviousWasEmpty,
  MachineryRhFormState,
  ShorePowerState,
} from '@navstation-portal/reporting/data-model/vessel-report';
import {
  FlowMeter,
  FlowMeterInstallation,
  FlowMeterInstallationType,
  PrimeMoverTypes,
  VesselSpec,
} from '@navstation-portal/vessels/data-model/vessel';
import { getFieldValueFromFormState, isSectionValid, isStateField } from '../vm-builder-helpers';

const MAIN_FUEL_AND_GAS_TYPES: string[] = [
  FlowMeterInstallationType.TwoLines,
  FlowMeterInstallationType.DuelFuelEngineMESingleLine,
  FlowMeterInstallationType.DuelFuelEngineMEAESingleLine,
  FlowMeterInstallationType.DualFuelEngineMEAEDifferential,
];

const BOILER_FUEL_AND_GAS_TYPES: string[] = [
  FlowMeterInstallationType.InletAB12DiffCB,
  FlowMeterInstallationType.GasInletAB1,
  FlowMeterInstallationType.GasInletAB12,
];

const flowMetersOf = (installation: FlowMeterInstallation | undefined): FlowMeter[] =>
  Object.values(installation?.flow_meters ?? {});

const gasFlowMetersOf = (installation: FlowMeterInstallation | undefined): FlowMeter[] =>
  Object.values(installation?.gas_flow_meters ?? {});

const installationHasType = (installation: FlowMeterInstallation | undefined, types: string[]) =>
  installation != null && types.includes(installation._cls);

const isDualEngine = (vesselSpec: VesselSpec | undefined) => {
  if (!vesselSpec) return false;

  return vesselSpec.engine.number_of_main_engines > 1;
};

const getMainEngineMaxPower = (vesselSpec: VesselSpec | undefined) => {
  if (!vesselSpec) return;

  const mainEngine = vesselSpec.engine.me_engines[0];
  if (!mainEngine) return;

  const mcr = vesselSpec.engine.me_engines[0]?.mcr;
  return mcr;
};

// V1 gives a load as a percent of the main engine 1 MCR, in the range 0 to
// 100, and rounds only the display (tabs.directive.ts:3964-3990 in
// ../TVA-Reporting).
const calcMcrPercent = (power: number, mcr: number) => Math.min(Math.max((power * 100) / mcr, 0), 100);

const calcMainEngineLoad = (formState: FormState, vesselSpec: VesselSpec | undefined) => {
  const mcr = getMainEngineMaxPower(vesselSpec);
  if (mcr == null) return;

  const mainEngineSection = formState['mainEngine'];
  if (!mainEngineSection) return;

  const entry = mainEngineSection['power'];
  if (!isStateField(entry)) return;

  const power = entry.value as number;
  if (power == null) return;

  return calcMcrPercent(power, mcr);
};

const calcMainEngineRunningHours = (formState: FormState) => {
  const machineryRh = formState['machineryRunningHours'];
  if (!machineryRh) return;

  const entry = machineryRh['mainEngines'];
  if (isStateField(entry)) return;

  const mainEngine1 = entry[0];
  if (!mainEngine1) return;

  const current = mainEngine1['current'].value as number;
  if (current == null) return;

  const previous = mainEngine1['previous'].value as number;
  if (previous == null) return;

  return current - previous;
};

// V1 gives 0 while a counter is empty (tabs.directive.ts:2624-2633 in
// ../TVA-Reporting). V2 gives no value, so the report saves no false 0.
const calcMainEngineRevCounterDiff = (formState: FormState) => {
  const mainEngineSection = formState['mainEngine'];
  if (!mainEngineSection) return;

  const currentEntry = mainEngineSection['revCounterCurrent'];
  if (!isStateField(currentEntry)) return;
  const current = currentEntry.value as number;
  if (current == null) return;

  const previousEntry = mainEngineSection['revCounterPrevious'];
  if (!isStateField(previousEntry)) return;
  const previous = previousEntry.value as number;
  if (previous == null) return;

  return current - previous;
};

const calcShaftPowerMeterCounterDiff = (formState: FormState) => {
  const mainEngineSection = formState['mainEngine'];
  if (!mainEngineSection) return;

  const currentEntry = mainEngineSection['shaftPowerMeterCounterCurrent'];
  if (!isStateField(currentEntry)) return;
  const current = currentEntry.value as number;
  if (current == null) return;

  const previousEntry = mainEngineSection['shaftPowerMeterCounterPrevious'];
  if (!isStateField(previousEntry)) return;
  const previous = previousEntry.value as number;
  if (previous == null) return;

  return current - previous;
};

const getRhDiff = (item: MachineryRhFormState) => {
  const curr = item.current?.value;
  if (curr == null || isNaN(curr)) return;
  const prev = item.previous?.value;
  if (prev == null || isNaN(prev)) return;

  return curr - prev;
};

// The main engine Differences come from the readings, not from the store. The
// store holds a Difference only after this vm gives it, and an import clears
// the stored Difference.
const calcMainEngineRhDiffMax = (formState: FormState) => {
  const machineryRh = formState['machineryRunningHours'];
  if (!machineryRh) return;

  const entry = machineryRh['mainEngines'];
  if (isStateField(entry)) return;

  const diffs = entry
    .map((mainEngine) => getRhDiff(mainEngine as MachineryRhFormState))
    .filter((diff): diff is number => diff != null);
  if (diffs.length === 0) return;

  return Math.max(...diffs);
};

const calcShaftPowerMeterCounterCalculated = (shaftPowerMeterCounterDiff: number | undefined, formState: FormState) => {
  if (shaftPowerMeterCounterDiff == null || shaftPowerMeterCounterDiff <= 0) return;

  const mainEngineRhDiffMax = calcMainEngineRhDiffMax(formState);
  if (!mainEngineRhDiffMax) return;

  return shaftPowerMeterCounterDiff / mainEngineRhDiffMax;
};

// V1 has one average power input. It holds the calculated value until the user
// of a vessel that reports the average power by hand types over it
// (tabs.directive.ts:2891-2913 in ../TVA-Reporting). V2 keeps the typed value
// in its own field, so the field counts only on such a vessel: a saved V1
// report holds the calculated value under the same key.
const getAveragePower = (
  shaftPowerMeterCounterCalculated: number | undefined,
  formState: FormState,
  vesselSpec: VesselSpec | undefined
) => {
  if (!vesselSpec?.information?.manually_report_calculated_average_total_power) {
    return shaftPowerMeterCounterCalculated;
  }

  const typed = getFieldValueFromFormState(formState, 'mainEngine', 'shaftPowerMeterCounterCalculatedManual') as
    | number
    | undefined;
  return typed ?? shaftPowerMeterCounterCalculated;
};

// V1 adds 3% to the average power (tabs.directive.ts:2883 in
// ../TVA-Reporting).
const calcMainEngines1And2Power = (averagePower: number | undefined) => {
  if (averagePower == null) return;

  return averagePower * 1.03;
};

const calcMainEngines1And2Load = (mainEngines1And2Power: number | undefined, vesselSpec: VesselSpec | undefined) => {
  if (mainEngines1And2Power == null) return;

  const mcr = getMainEngineMaxPower(vesselSpec);
  if (mcr == null) return;

  return calcMcrPercent(mainEngines1And2Power, mcr);
};

const calcDifference = (formState: FormState, sectionKey: string, property: string) => {
  const section = formState[sectionKey];
  if (!section) return;

  const currentEntry = section[`${property}Curr`];
  if (!isStateField(currentEntry)) return;

  const current = currentEntry?.value as number;
  if (current == null) return;

  const prevEntry = section[`${property}Prev`];
  if (!isStateField(prevEntry)) return;
  const previous = prevEntry.value as number;
  if (previous == null) return;

  return current - previous;
};

// Revolutions per minute, as V1 gives it (tabs.directive.ts:2640 in
// ../TVA-Reporting). V1 gives 0 when the engine ran for 0 hours.
const calcRpm = (revDiff?: number, runningHours?: number) => {
  if (revDiff == null || runningHours == null) return;
  if (runningHours === 0) return 0;

  return revDiff / (runningHours * 60);
};

// A previous reading is open to the user on a vessel's first report, because
// no earlier report filled it, and wherever the previous report left it empty.
//
// A report number of 1 is how this knows a report is the first. The form-rule
// template sets that number, not this tab: the carryOver entry on
// operational.vesselDetails.reportNumber gives 1 through its else branch when
// no previous report exists. An edit to that entry can lock these fields, so
// the dependency is pinned in carry-over-on-create.integration.spec.ts. See
// the Carry-over section of docs/agents/patterns/form-rule-language.md.
const canEditPreviousValue = (reportNumber: number | undefined, item: HasPreviousWasEmpty) => {
  return reportNumber === 1 || !!item.previousWasEmpty?.value;
};

const isDieselElectricPropulsion = (vesselSpec: VesselSpec | undefined): boolean =>
  vesselSpec?.information?.prime_mover_type === PrimeMoverTypes.DIESEL_ELECTRIC_PROPULSION;

const getAuxBoilerTotalRh = (formState: FormState, vesselSpec: VesselSpec | undefined) => {
  if (!isDieselElectricPropulsion(vesselSpec)) return;

  const machineryRh = formState['machineryRunningHours'];
  if (!machineryRh) return;

  const entry = machineryRh['auxBoilers'];
  if (isStateField(entry)) return;

  return entry.reduce((total, auxBoiler) => {
    const current = auxBoiler['current']?.value as number | undefined;
    const previous = auxBoiler['previous']?.value as number | undefined;
    if (current == null || previous == null) return total;

    return total + (current - previous);
  }, 0);
};

const isAuxBoilerTotalRhInvalid = (totalRh: number | undefined, reportPeriod: number | undefined) => {
  if (totalRh == null || reportPeriod == null) return false;

  return totalRh > reportPeriod + 1;
};

const calcAuxBoilerTotalRhWarning = (totalRh: number | undefined, reportPeriod: number | undefined) => {
  if (!isAuxBoilerTotalRhInvalid(totalRh, reportPeriod)) return;

  return `Total Auxiliary Boiler running hours must not exceed the reporting period by more than 1 hour, currently it is ${totalRh} hours but the reporting period is ${reportPeriod} hours`;
};

const getShorePowerHours = (shorePower: ShorePowerState | undefined, reportFrom?: Date, reportTo?: Date) => {
  if (!shorePower?.isUsed) return;

  const start = shorePower.startTime;
  if (!start) return;

  const end = shorePower.endTime ?? reportTo;
  if (!end) return;

  const durationStart = reportFrom && reportFrom > start ? reportFrom : start;
  const diffMs = end.getTime() - durationStart.getTime();
  if (diffMs < 0) return;

  return diffMs / (1000 * 60 * 60);
};

const getShorePowerKwhMax = (hours: number | undefined, vesselSpec: VesselSpec | undefined) => {
  if (!hours) return 0;

  if (isDieselElectricPropulsion(vesselSpec)) {
    const dieselGenerator = vesselSpec?.prime_mover?.diesel_generators;
    const numberOfDieselGenerators = vesselSpec?.prime_mover?.number_of_diesel_generators ?? 0;
    if (!dieselGenerator?.mcr) return 0;

    return dieselGenerator.mcr * numberOfDieselGenerators * hours;
  }

  const engines = vesselSpec?.engine?.ae_engines ?? [];
  if (!engines.length) return 0;

  const totalMcr = engines.reduce((sum, engine) => sum + (engine.mcr ?? 0), 0);
  return totalMcr * hours;
};

const getShorePowerStartTimeWarning = (shorePower: ShorePowerState | undefined, reportTo: Date | undefined) => {
  if (!shorePower?.isUsed) return;

  const start = shorePower.startTime;
  if (!start) return;

  if (!reportTo) return 'Must be before report to time and report to time is not set';
  if (start > reportTo) return 'Must be before report end time';

  return;
};

const getShorePowerEndTimeWarning = (
  shorePower: ShorePowerState | undefined,
  reportFrom: Date | undefined,
  reportTo: Date | undefined
) => {
  if (!shorePower?.isUsed) return;

  const end = shorePower.endTime;
  if (!end) return;

  if (!reportFrom) return 'Must be after report start time and report from time is not set';
  if (end < reportFrom) return 'Must be after report from time';

  const start = shorePower.startTime;
  if (!start) return 'Must be after shore power start time and it is not set';
  if (end < start) return 'Must be after shore power start time';

  if (!reportTo) return 'Must be before report to time and it is not set';
  if (end > reportTo) return 'Must be before report to time';

  return;
};

const getExpectedTotalShorePowerKwh = (shorePower: ShorePowerState | undefined) => {
  const kwh = shorePower?.kwh;
  if (kwh == null || isNaN(kwh)) return;

  const runningTotal = shorePower?.runningTotalKwh;
  if (runningTotal == null || isNaN(runningTotal)) return kwh;

  return kwh + runningTotal;
};

const isShorePowerKwhValid = (shorePower: ShorePowerState | undefined) => {
  if (!shorePower?.isUsed) return true;

  return shorePower.kwh != null && shorePower.kwh > 0;
};

const isShorePowerHoursValid = (shorePower: ShorePowerState | undefined, hours: number | undefined) => {
  if (!shorePower?.isUsed) return true;

  return hours != null && hours > 0;
};

const unwrapShorePower = (formState: FormState): ShorePowerState => {
  const section = formState['shorePower'];
  if (!section) return {};

  const result: ShorePowerState = {};
  for (const [key, entry] of Object.entries(section)) {
    if (isStateField(entry)) result[key] = entry.value;
  }

  return result;
};

const getCargoPumpMeterDiff = (item: CargoPumpMeterFormState): number => {
  const cur = item.cur?.value;
  const prev = item.prev?.value;
  if (cur == null || isNaN(cur) || prev == null || isNaN(prev)) return 0;

  return cur - prev;
};

export function buildPowerVm(
  formState: FormState,
  vesselSpec: VesselSpec | undefined,
  reportPeriod: number | undefined,
  reportNumber: number | undefined,
  reportFrom?: Date,
  reportTo?: Date
): PowerVm {
  const auxBoilerTotalRh = getAuxBoilerTotalRh(formState, vesselSpec);
  const auxBoilerTotalRhInvalid = isAuxBoilerTotalRhInvalid(auxBoilerTotalRh, reportPeriod);

  const isMainEngineValid = isSectionValid(formState['mainEngine']);
  const isMachineryRhValid = isSectionValid(formState['machineryRunningHours']) && !auxBoilerTotalRhInvalid;
  const isFlowMeterInstallationOldValid = isSectionValid(formState['flowMeterInstallationOld']);
  const isPumpsValid = isSectionValid(formState['pumps']);
  const isGeneratorEnginesValid = isSectionValid(formState['generatorEngines']);
  const isShaftGeneratorValid = isSectionValid(formState['shaftGeneratorPTO']);
  const isScrubberValid = isSectionValid(formState['scrubber']);
  const isFlowMeterInstallationValid = isSectionValid(formState['flowMeterInstallation'], true);
  const isEnvironmentalValid = isSectionValid(formState['environmental']);

  const shorePower = unwrapShorePower(formState);
  const shorePowerHours = getShorePowerHours(shorePower, reportFrom, reportTo);
  const shorePowerKwhMax = getShorePowerKwhMax(shorePowerHours, vesselSpec);
  const expectedTotalShorePowerKwh = getExpectedTotalShorePowerKwh(shorePower);
  const shorePowerKwhValid = isShorePowerKwhValid(shorePower);
  const shorePowerHoursValid = isShorePowerHoursValid(shorePower, shorePowerHours);
  const isShorePowerValid = isSectionValid(formState['shorePower'], true) && shorePowerKwhValid && shorePowerHoursValid;
  const isCargoPumpMetersValid = isSectionValid(formState['cargoPumpMeters'], true);
  const cargoPumpMeterPumps = formState['cargoPumpMeters']?.['pumps'] as FormSectionStateArray | undefined;
  const hasCargoPumpMeters = (cargoPumpMeterPumps?.length ?? 0) > 0;

  // Shore power is left out: the report store adds it only where the section
  // shows, and the Power store cannot tell where that is.
  const isPowerValidExceptShorePower =
    isMainEngineValid &&
    isMachineryRhValid &&
    isFlowMeterInstallationOldValid &&
    isPumpsValid &&
    isGeneratorEnginesValid &&
    isShaftGeneratorValid &&
    isScrubberValid &&
    isFlowMeterInstallationValid &&
    isEnvironmentalValid &&
    isCargoPumpMetersValid;

  const mainInstallation = vesselSpec?.main_flow_meter_installation;
  const boilerInstallation = vesselSpec?.boiler_flow_meter_installation;

  const mainFlowMeters = flowMetersOf(mainInstallation);
  const boilerFlowMeters = flowMetersOf(boilerInstallation);
  const mainGasFlowMeters = gasFlowMetersOf(mainInstallation);
  const boilerGasFlowMeters = gasFlowMetersOf(boilerInstallation);

  const showFlowMeterInstallationNew =
    mainFlowMeters.length > 0 ||
    boilerFlowMeters.length > 0 ||
    mainGasFlowMeters.length > 0 ||
    boilerGasFlowMeters.length > 0;

  const getFlowMeterInstallationDiff = (currPath: string, prevPath: string) => {
    const curr = getFieldValueFromFormState(formState, 'flowMeterInstallation', currPath) as number | undefined;
    if (curr == null) return;

    const prev = getFieldValueFromFormState(formState, 'flowMeterInstallation', prevPath) as number | undefined;
    if (prev == null) return;

    return curr - prev;
  };

  const mainEngineRunningHours = calcMainEngineRunningHours(formState);
  const mainEngineRevCounterDiff = calcMainEngineRevCounterDiff(formState);

  const shaftPowerMeterCounterDiff = calcShaftPowerMeterCounterDiff(formState);
  const shaftPowerMeterCounterCalculated = calcShaftPowerMeterCounterCalculated(shaftPowerMeterCounterDiff, formState);
  const mainEngines1And2Power = calcMainEngines1And2Power(
    getAveragePower(shaftPowerMeterCounterCalculated, formState, vesselSpec)
  );

  const foBoosterDiff = calcDifference(formState, 'flowMeterInstallationOld', 'foBooster');
  const gasOilBoosterDiff = calcDifference(formState, 'flowMeterInstallationOld', 'gasOilBooster');
  const meAeInletDiff = calcDifference(formState, 'flowMeterInstallationOld', 'meAeInlet');
  const auxEngineInletDiff = calcDifference(formState, 'flowMeterInstallationOld', 'auxEngineInlet');
  const auxEngineOutletDiff = calcDifference(formState, 'flowMeterInstallationOld', 'auxEngineOutlet');
  const auxBoiler1Diff = calcDifference(formState, 'flowMeterInstallationOld', 'auxBoiler1');
  const cylinderLubeOilDiff = calcDifference(formState, 'flowMeterInstallationOld', 'cylinderLubeOil');

  const meLoPump1Diff = calcDifference(formState, 'pumps', 'meLoPump1');
  const meLoPump2Diff = calcDifference(formState, 'pumps', 'meLoPump2');
  const seaWaterPump1Diff = calcDifference(formState, 'pumps', 'seaWaterPump1');
  const seaWaterPump2Diff = calcDifference(formState, 'pumps', 'seaWaterPump2');
  const sgPump1Diff = calcDifference(formState, 'pumps', 'sgPump1');
  const sgPump2Diff = calcDifference(formState, 'pumps', 'sgPump2');
  const foPurifier1Diff = calcDifference(formState, 'pumps', 'foPurifier1');
  const foPurifier2Diff = calcDifference(formState, 'pumps', 'foPurifier2');
  const erFan1Diff = calcDifference(formState, 'pumps', 'erFan1');
  const erFan2Diff = calcDifference(formState, 'pumps', 'erFan2');
  const erFan3Diff = calcDifference(formState, 'pumps', 'erFan3');
  const erFan4Diff = calcDifference(formState, 'pumps', 'erFan4');

  // The adapter skips an aux engine that holds no data, so find a row by its
  // key, for example 'auxEngine3', and not by its index.
  const findAuxEngineRow = (sectionId: string, rowKey: string) => {
    const section = formState[sectionId];
    if (!section) return;

    const entries = section['auxEngines'];
    if (isStateField(entries)) return;

    return entries.find((entry) => entry['key']?.value === rowKey);
  };

  const getGeMaxRating = (rowKey: string) => {
    const row = findAuxEngineRow('generatorEngines', rowKey);
    if (!row) return;

    const value = row['power'].value as number;
    if (value == null) return;

    const engineNumber = Number(rowKey.replace('auxEngine', ''));
    const engineSpec = vesselSpec?.engine.ae_engines[engineNumber - 1];
    if (!engineSpec?.mcr) return;

    return (value / engineSpec.mcr) * 100;
  };

  const getGeRunningHours = (rowKey: string) => {
    const row = findAuxEngineRow('machineryRunningHours', rowKey);
    if (!row) return;

    return getRhDiff(row as MachineryRhFormState);
  };

  const getSgMaxRating = () => {
    const shaftGeneratorSection = formState['shaftGeneratorPTO'];
    if (!shaftGeneratorSection) return;

    const entry = shaftGeneratorSection['power'];
    if (!isStateField(entry)) return;

    const power = entry.value as number;
    if (power == null) return;
    const mcr = vesselSpec?.engine.pto?.mcr;
    if (!mcr) return;

    return (power / mcr) * 100;
  };

  const getSgRunningHours = () => {
    const machineryRhSection = formState['machineryRunningHours'];
    if (!machineryRhSection) return;

    const entries = machineryRhSection['shaftGeneratorPTO'];
    if (isStateField(entries) || !entries[0]) return;

    return getRhDiff(entries[0] as MachineryRhFormState);
  };

  const getMainEngine1RhDiff = () => {
    const machineryRhSection = formState['machineryRunningHours'];
    if (!machineryRhSection) return;

    const entries = machineryRhSection['mainEngines'];
    if (isStateField(entries) || !entries[0]) return;

    return getRhDiff(entries[0] as MachineryRhFormState);
  };

  const getSgRunningHoursMax = () => {
    if (reportPeriod == null) return;

    // shaft motor (PTI) running hours aren't modelled yet, so it's treated as always 0
    const ptiRunningHours = 0;
    const mainEngine1DiffRh = getMainEngine1RhDiff();

    if (ptiRunningHours === 0 && mainEngine1DiffRh != null) {
      return Math.ceil(mainEngine1DiffRh);
    }

    return Math.ceil(reportPeriod - ptiRunningHours);
  };

  const calcTotalLo = () => {
    if (!vesselSpec) return;

    const generatorEngineSection = formState['generatorEngines'];
    if (!generatorEngineSection) return;

    const entry = generatorEngineSection['auxEngines'];
    if (isStateField(entry)) return;

    return entry.reduce((acc, auxEngine) => {
      const loAdded = auxEngine['loAdded'].value as number;
      if (!loAdded || isNaN(loAdded)) return acc;
      return acc + loAdded;
    }, 0);
  };

  const isFieldInvalid = (sectionKey: string, propertyKey: string): boolean => {
    const section = formState[sectionKey];
    if (!section) return false;

    const property = section[propertyKey];
    if (!property) return false;

    if (!isStateField(property)) return false;

    return property.status === 'INVALID';
  };

  return {
    // main engine
    isDualEngine: isDualEngine(vesselSpec),
    mainEngineMaxPower: getMainEngineMaxPower(vesselSpec),
    mainEngineLoad: calcMainEngineLoad(formState, vesselSpec),
    mainEngineRunningHours,
    mainEngineRevCounterDiff,
    // V1 divides by the running hours with no rounding (tabs.directive.ts:2928
    // in ../TVA-Reporting).
    mainEngineCalculatedRpm: calcRpm(mainEngineRevCounterDiff, getMainEngine1RhDiff()),
    shaftPowerMeterCounterDiff,
    shaftPowerMeterCounterCalculated,
    mainEngines1And2Power,
    mainEngines1And2Load: calcMainEngines1And2Load(mainEngines1And2Power, vesselSpec),
    mainEngines1And2RunningHours: calcMainEngineRhDiffMax(formState),

    // machinery rh
    getRhDiff,
    auxBoilerTotalRhWarning: calcAuxBoilerTotalRhWarning(auxBoilerTotalRh, reportPeriod),
    isAuxBoilerTotalRhInvalid: auxBoilerTotalRhInvalid,

    // marine cargo pump power meter
    getCargoPumpMeterDiff,
    hasCargoPumpMeters,

    // flow meters
    foBoosterDiff,
    gasOilBoosterDiff,
    meAeInletDiff,
    auxEngineInletDiff,
    auxEngineOutletDiff,
    auxBoiler1Diff,
    cylinderLubeOilDiff,

    // pumps
    meLoPump1Diff,
    meLoPump2Diff,
    seaWaterPump1Diff,
    seaWaterPump2Diff,
    sgPump1Diff,
    sgPump2Diff,
    foPurifier1Diff,
    foPurifier2Diff,
    erFan1Diff,
    erFan2Diff,
    erFan3Diff,
    erFan4Diff,

    // generator engines
    getGeMaxRating,
    getGeRunningHours,
    totalLoAdded: calcTotalLo(),

    // shaft generators
    getSgMaxRating,
    getSgRunningHours,
    getSgRunningHoursMax,

    // flow meter installation (new)
    showFlowMeterInstallationNew,
    showFlowMeterInstallationOld: !showFlowMeterInstallationNew,
    mainFlowMeterHasFuelAndGas: installationHasType(mainInstallation, MAIN_FUEL_AND_GAS_TYPES),
    boilerFlowMeterHasFuelAndGas: installationHasType(boilerInstallation, BOILER_FUEL_AND_GAS_TYPES),
    mainFlowMeters,
    boilerFlowMeters,
    mainGasFlowMeters,
    boilerGasFlowMeters,
    getFlowMeterInstallationDiff,

    // validity
    isFieldInvalid,
    isMainEngineValid,
    isMachineryRhValid,
    isFlowMeterInstallationOldValid,
    isPumpsValid,
    isGeneratorEnginesValid,
    isShaftGeneratorValid,
    isScrubberValid,
    isFlowMeterInstallationValid,
    isEnvironmentalValid,
    isCargoPumpMetersValid,
    isPowerValidExceptShorePower,
    isDieselElectricPropulsion: isDieselElectricPropulsion(vesselSpec),

    canEditPreviousValue: (item: HasPreviousWasEmpty) => canEditPreviousValue(reportNumber, item),

    // shore power
    shorePowerHours,
    shorePowerKwhMax,
    shorePowerStartTimeWarning: getShorePowerStartTimeWarning(shorePower, reportTo),
    shorePowerEndTimeWarning: getShorePowerEndTimeWarning(shorePower, reportFrom, reportTo),
    expectedTotalShorePowerKwh,
    showTotalShorePowerModifiedWarning:
      shorePower.totalKwh != null &&
      expectedTotalShorePowerKwh != null &&
      shorePower.totalKwh !== expectedTotalShorePowerKwh,
    isShorePowerKwhValid: shorePowerKwhValid,
    isShorePowerHoursValid: shorePowerHoursValid,
    isShorePowerValid,
  };
}
